import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, FileText, ChevronRight, ChevronDown, Trash2, Eye, Star, Pencil, Sparkles, Play, Pause } from 'lucide-react'
import { useCaseStore } from '../../stores'
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import { useI18n } from '../../i18n'
import { isDescribeLocked, hasWrittenSummary } from '../../utils/describeLock'
import { caseIsShared } from '../../utils/summaryNext'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import { aiAPI } from '../../services/api'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import ProfileCheckCard from '../../components/ProfileCheckCard'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// Color constants
const NAVY = '#15354F'

export default function DescribeReview() {
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase, deleteCase } = useCaseStore()
  const { t, language } = useI18n()
  const { tq, to } = useQuestionnaireTranslation()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [navigatingToSummary, setNavigatingToSummary] = useState(false)
  // v164.3.0: Post-submission expand/collapse for reviewing answers
  const [expandedSection, setExpandedSection] = useState(null)
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showContent, setShowContent] = useState(() => {
    return !!sessionStorage.getItem(`orella_chat_review_${currentCase?.id}`)
  })
  
  // v164.3.0: Recover showContent if it was initialized false because currentCase was null on mount
  // (Zustand rehydration race). Once currentCase arrives, check sessionStorage with correct key.
  useEffect(() => {
    if (currentCase?.id && !showContent) {
      const hasPlayed = sessionStorage.getItem(`orella_chat_review_${currentCase.id}`)
      if (hasPlayed) {
        setShowContent(true)
      }
    }
  }, [currentCase?.id])
  
  // Check if already submitted (locked)
  // v242.235.0: answers lock once the summary is written (utils/describeLock.js owns the rule).
  const isLocked = isDescribeLocked(currentCase)

  // v227.10.0: Profile Check — surface conflicts between the case (voice + answers)
  // and the saved profile, for reconciliation before submission. Detection is cached
  // on the case (pre_visit_summary.profileCheck) so re-opening Review doesn't re-spend.
  const [pcItems, setPcItems] = useState(() => currentCase?.preVisitSummary?.profileCheck?.discrepancies || [])
  const [pcResolved, setPcResolved] = useState(() => currentCase?.preVisitSummary?.profileCheck?.resolved || {})
  const [pcLoading, setPcLoading] = useState(false)
  const pcRanRef = useRef(false)
  const pcResultRef = useRef(currentCase?.preVisitSummary?.profileCheck || null)

  useEffect(() => {
    if (isLocked || !currentCase?.id || pcRanRef.current) return
    pcRanRef.current = true
    const cached = currentCase?.preVisitSummary?.profileCheck
    if (cached && Array.isArray(cached.discrepancies)) {
      pcResultRef.current = cached
      setPcItems(cached.discrepancies)
      if (cached.resolved) setPcResolved(cached.resolved)
      return
    }
    setPcLoading(true)
    aiAPI.detectProfileDiscrepancies(currentCase.id)
      .then((res) => {
        if (res && Array.isArray(res.discrepancies)) {
          pcResultRef.current = res
          setPcItems(res.discrepancies)
        }
      })
      .catch((err) => console.log('[DescribeReview] profile check skipped:', err?.message))
      .finally(() => setPcLoading(false))
  }, [currentCase?.id, isLocked])

  const handlePcChange = (nextResolved) => {
    setPcResolved(nextResolved)
    // Persist resolved durably (fire-and-forget; non-blocking) so it survives re-open.
    try {
      const base = pcResultRef.current || { discrepancies: pcItems }
      const existingPvs = currentCase?.preVisitSummary || {}
      updateAndSaveCase({ preVisitSummary: { ...existingPvs, profileCheck: { ...base, resolved: nextResolved } } })
    } catch (e) { /* non-blocking */ }
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentCase?.id])

  // ★★ s98 D469 — THE REVIEW-SCREEN SUMMARY PREFETCH THAT LIVED HERE IS GONE, for the reason
  //   v242.235.0 removed DescribeAdaptive's: the server already prewarms the summary when the
  //   adaptive answers land (update_case dispatches `patient_summary`, SUMMARY-GW-TIMEOUT-1), so
  //   this call, landing ~1 s later on another Lambda, missed the not-yet-written column and ran a
  //   SECOND full generation (Haiku pre-visit summary + patient education) beside the prewarm.
  //   Measured on Dr. Rami's cases 2026-10-10: every case paid for both, in parallel. ViewSummary's
  //   stream cache-hits the prewarmed column; its own fallback still covers a missing one.

  // FHOOK: hook above the early return (rules of hooks); the store hook never reads currentCase.
  const { getSpecialty, getFocusArea } = useQuestionnaireStore()

  if (!currentCase) {
    navigate('/home')
    return null
  }

  const answers = currentCase.answers || {}
  
  // Get questions for current case
  const specialty = getSpecialty(currentCase.specialtyId)
  const focusArea = getFocusArea(currentCase.specialtyId, currentCase.focusAreaId)
  
  // Check if any questions in a category have answers
  const hasAnswersInCategory = (categoryKey) => {
    const questions = focusArea?.questions?.[categoryKey] || []
    if (questions.length === 0) return false
    return questions.some(q => {
      const answerKey = `${categoryKey}_${q.id}`
      return answers[answerKey]?.length > 0
    })
  }

  // Count answered questions in a category
  const countAnswers = (categoryKey) => {
    const questions = focusArea?.questions?.[categoryKey] || []
    if (questions.length === 0) return 0
    return questions.filter(q => {
      const answerKey = `${categoryKey}_${q.id}`
      return answers[answerKey]?.length > 0
    }).length
  }

  // Get a brief summary of answers in a category
  // v177.4.0: Apply to() for i18n — answer summaries display in user's language
  const getAnswerSummary = (categoryKey) => {
    const questions = focusArea?.questions?.[categoryKey] || []
    const summaryParts = []
    questions.forEach(q => {
      const answerKey = `${categoryKey}_${q.id}`
      const val = answers[answerKey]
      if (val && val.length > 0) {
        if (Array.isArray(val)) {
          summaryParts.push(val.map(v => to(v)).join(' · '))
        } else {
          summaryParts.push(to(String(val)))
        }
      }
    })
    const joined = summaryParts.join(' · ')
    return joined.length > 80 ? joined.substring(0, 77) + '...' : joined
  }
  
  // Section data
  const sections = [
    { 
      id: 'voice', 
      name: t('describe.voiceRecording'), 
      complete: currentCase.hasVoiceNote === true || !!currentCase.voiceTextInput,
      badge: currentCase.hasVoiceNote 
        ? (t('describe.recorded') || 'Recorded') 
        : currentCase.voiceTextInput 
          ? (t('describe.textNote') || 'Text Note') 
          : (t('common.skipped') || 'Skipped'),
      summary: currentCase.hasVoiceNote 
        ? `${t('describe.voiceMode')} · ${currentCase.voiceRecordingDuration || ''}` 
        : currentCase.voiceTextInput 
          ? `${t('describe.textMode') || 'Text mode'}` 
          : '',
      path: '/case/describe/voice' 
    },
    { 
      id: 'details', 
      name: t('describe.details'), 
      complete: hasAnswersInCategory('details'),
      badge: hasAnswersInCategory('details') ? t('describeReview.answeredCount', { count: countAnswers('details') }) : (t('common.skipped') || 'Skipped'),
      summary: getAnswerSummary('details'),
      path: '/case/describe/details' 
    },
    { 
      id: 'signs', 
      name: t('describe.signsSymptoms'), 
      complete: hasAnswersInCategory('signs_symptoms'),
      badge: hasAnswersInCategory('signs_symptoms') ? t('describeReview.selectedCount', { count: countAnswers('signs_symptoms') }) : (t('common.skipped') || 'Skipped'),
      summary: getAnswerSummary('signs_symptoms'),
      path: '/case/describe/signs-symptoms' 
    },
    { 
      id: 'context', 
      name: t('describe.context'), 
      complete: hasAnswersInCategory('context'),
      badge: hasAnswersInCategory('context') ? t('describeReview.answeredCount', { count: countAnswers('context') }) : (t('common.skipped') || 'Skipped'),
      summary: getAnswerSummary('context'),
      path: '/case/describe/context' 
    },
    { 
      id: 'additional', 
      name: t('describe.additionalFactors'), 
      complete: hasAnswersInCategory('additional_factors'),
      badge: hasAnswersInCategory('additional_factors') ? t('describeReview.selectedCount', { count: countAnswers('additional_factors') }) : (t('common.skipped') || 'Skipped'),
      summary: getAnswerSummary('additional_factors'),
      path: '/case/describe/additional' 
    },
    (() => {
      const aqs = currentCase?.adaptiveQuestions || currentCase?.adaptive_questions
      const ans = currentCase?.adaptiveAnswers || currentCase?.adaptive_answers
      const qs = aqs?.questions || (Array.isArray(aqs) ? aqs : [])
      const qCount = qs.length
      // v214.9.0 (Bug 3 + 4): handle array values (multi-select) in addition to strings,
      // and switch to a "X answered" + plain summary copy instead of the misleading
      // "X of N AI-tailored questions" (the N includes the optional free-text question
      // most patients skip, which made it look like answers were missing).
      const aCount = ans ? Object.keys(ans).filter(k => {
        const v = ans[k]
        if (Array.isArray(v)) return v.length > 0
        if (typeof v === 'string') return v.trim().length > 0
        return !!v
      }).length : 0
      return {
        id: 'adaptive',
        name: t('describe.followUpQuestions') || 'Follow-up Questions',
        complete: aCount > 0,
        badge: aCount > 0 ? t('describeReview.answeredCount', { count: aCount }) : qCount > 0 ? (t('common.pending') || 'Pending') : (t('common.skipped') || 'Skipped'),
        summary: aCount > 0 ? t('describeReview.personalizedSummary') : qCount > 0 ? t('describeReview.personalizedReady') : '',
        path: '/case/describe/adaptive',
        icon: 'sparkles'
      }
    })(),
  ]

  // v164.3.0: Get Q&A pairs for a questionnaire category (for locked expand view)
  // v177.4.0: Apply tq/to for i18n — questions and answers display in user's language
  const getQAPairs = (categoryKey) => {
    const questions = focusArea?.questions?.[categoryKey] || []
    return questions.map(q => {
      const answerKey = `${categoryKey}_${q.id}`
      const val = answers[answerKey]
      if (!val || (Array.isArray(val) && val.length === 0)) return null
      return {
        question: tq(q.text || q.label || q.id),
        answer: Array.isArray(val) ? val.map(v => to(v)).join(' · ') : to(String(val))
      }
    }).filter(Boolean)
  }

  // Toggle expand for locked sections
  const toggleExpand = (sectionId) => {
    setExpandedSection(prev => prev === sectionId ? null : sectionId)
  }

  // Render expanded content for each section type
  const renderExpandedContent = (section) => {
    if (section.id === 'voice') {
      const voiceUrl = currentCase.voiceRecording?.url || currentCase.voiceRecording?.audioData
      const voiceText = currentCase.voiceTextInput
      return (
        <div className="mt-2 space-y-2">
          {voiceUrl && (
            <div className="flex items-center gap-2 bg-white rounded-lg p-2.5 border border-gray-100">
              <audio ref={audioRef} src={voiceUrl} className="hidden"
                onEnded={() => setIsPlaying(false)}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (!audioRef.current) return
                  isPlaying ? audioRef.current.pause() : audioRef.current.play()
                  setIsPlaying(!isPlaying)
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{ backgroundColor: '#15354F' }}
              >
                {isPlaying ? <Pause size={12} /> : <Play size={12} className="ml-0.5" />}
              </button>
              <span className="text-[12px] text-gray-500">
                {isPlaying ? t('describeReview.playing') : t('describeReview.tapToPlay')}
              </span>
            </div>
          )}
          {voiceText && (
            <div className="bg-white rounded-lg p-2.5 border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{t('describeReview.transcript')}</p>
              <p className="text-[12px] text-gray-600 leading-relaxed italic">"{voiceText}"</p>
            </div>
          )}
          {!voiceUrl && !voiceText && (
            <p className="text-[11px] text-gray-400 italic">{t('describeReview.noVoiceRecording')}</p>
          )}
        </div>
      )
    }

    if (section.id === 'adaptive') {
      const aqs = currentCase?.adaptiveQuestions || currentCase?.adaptive_questions
      const ans = currentCase?.adaptiveAnswers || currentCase?.adaptive_answers || {}
      const qs = aqs?.questions || (Array.isArray(aqs) ? aqs : [])
      if (qs.length === 0) return <p className="mt-2 text-[11px] text-gray-400 italic">{t('describeReview.noFollowupQuestions')}</p>
      return (
        <div className="mt-2 space-y-1.5">
          {qs.map((q, i) => {
            const a = ans[q.id]
            // v214.9.0 (Bug 3): multi-select answers come back as arrays — render with " · "
            const display = Array.isArray(a)
              ? (a.length > 0 ? a.join(' · ') : null)
              : (a && String(a).trim().length > 0 ? a : null)
            return (
              <div key={q.id} className="bg-white rounded-lg p-2.5 border border-gray-100">
                <p className="text-[11px] text-gray-500 mb-0.5">{i+1}. {q.text}</p>
                <p className="text-[12px] text-gray-700 font-medium">{display || <span className="text-gray-400 italic">{t('describeReview.noResponse')}</span>}</p>
              </div>
            )
          })}
        </div>
      )
    }

    // Standard questionnaire categories
    const categoryMap = { details: 'details', signs: 'signs_symptoms', context: 'context', additional: 'additional_factors' }
    const categoryKey = categoryMap[section.id]
    if (!categoryKey) return null
    const pairs = getQAPairs(categoryKey)
    if (pairs.length === 0) return <p className="mt-2 text-[11px] text-gray-400 italic">{t('describeReview.noResponses')}</p>
    return (
      <div className="mt-2 space-y-1.5">
        {pairs.map((p, i) => (
          <div key={i} className="bg-white rounded-lg p-2.5 border border-gray-100">
            <p className="text-[11px] text-gray-500 mb-0.5">{p.question}</p>
            <p className="text-[12px] text-gray-700 font-medium">{p.answer}</p>
          </div>
        ))}
      </div>
    )
  }

  const handleDeleteCase = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDeleteConfirm(false)
    navigate('/home', { replace: true })
    // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
    if (id) { try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) } }
  }
  
  const handleSaveAsDraft = async () => {
    if (currentCase?.id) {
      await updateAndSaveCase({ status: 'draft' })
    }
    setShowDeleteConfirm(false)
    navigate('/cases', { replace: true })
  }
  
  // ★★ v242.235.0 (session 96, Dr. Rami's mockup-B ruling) — THIS TAP LOCKS THE ANSWERS.
  //   The server stamps summary_locked_at; from then on the summary, once written, is final,
  //   and a summary prewarmed from answers changed here since is cleared and rebuilt. The
  //   lock is awaited (so the summary screen never builds from an unlocked case) but never
  //   blocks the patient: a failed lock still opens the summary.
  // ★★ s98 · D460: this tap no longer locks anything (the answers lock when a doctor accepts).
  //   The summary screen asks the server, which replays the summary when the answers are
  //   unchanged and rebuilds it ONCE when they changed.
  const hasSummary = hasWrittenSummary(currentCase)
  const sentNotAccepted = !isLocked && caseIsShared(currentCase)
  const handleViewSummary = async () => {
    setNavigatingToSummary(true)
    navigate('/case/describe/summary', { state: { fromReview: true } })
  }

  return (
    <div className="bg-white flex flex-col relative min-h-full">
      <ChatKeyframes />
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes reviewShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      `}</style>
      
      {/* Compact Header */}
      <CompactHeader
        stepNum={7}
        totalSteps={7}
        caseInfo={`${currentCase.specialtyName} › ${currentCase.focusAreaName}`}
        statusLabel={currentCase.status === 'finalized' ? t('caseHome.statusFinalized') : 
          currentCase.status === 'claimed' ? t('caseHome.statusClaimed') :
          currentCase.status === 'shared' ? t('caseHome.statusShared') :
          currentCase.status === 'submitted' ? t('cases.statusSubmitted') : t('caseHome.statusInProgress')}
        onBack={() => navigate(isLocked ? '/case/home' : '/case/describe/adaptive')}
        onNotification={() => navigate('/notifications')}
      />

      {/* Orella Chat */}
      <OrellaChatBubble
        message={isLocked ? t('describe.chatReviewAccepted') : sentNotAccepted ? t('describe.chatReviewSent') : (t('describe.chatReview') || "Here's a summary of everything you've shared. Take a moment to review it — this is what your doctor will see, so make sure it feels right.")}
        animationKey={`review_${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      {/* Content — slides up after chat animation */}
      <div className="flex-1">

        {/* v227.10.0: Profile Check — reconcile conflicts before submission */}
        {!isLocked && pcItems.length > 0 && showContent && (
          <div className="px-4 pt-3">
            <ProfileCheckCard items={pcItems} resolved={pcResolved} onChange={handlePcChange} />
          </div>
        )}
        {/* v242.38.0: Profile Review loader — the profile check runs in the background;
            without a placeholder a fast scroller reaches the bottom before it appears and
            misses it. Shows only while generating and before any result. */}
        {!isLocked && showContent && pcLoading && pcItems.length === 0 && (
          <div className="px-4 pt-3">
            <div className="rounded-2xl overflow-hidden mb-4" style={{ border: '1.5px solid #A7D9FC', background: '#fff' }}>
              <div className="flex items-start gap-2.5 px-3.5 py-3" style={{ background: '#E9F8FF' }}>
                <div className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center flex-shrink-0" style={{ background: '#fff', border: '1.5px solid #A7D9FC' }}>
                  <OrellaSpinner size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13.5px] font-bold" style={{ color: '#15354F' }}>{t('describeReview.profileReviewTitle', { defaultValue: 'Reviewing your profile' })}</p>
                  <p className="text-[11.5px] leading-snug mt-0.5" style={{ color: '#5B6E80' }}>{t('describeReview.profileReviewSub', { defaultValue: 'Checking your case against your saved health profile' })}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Answer Sections — no heavy summary card above */}
        <div 
          className="px-4 pt-2.5 space-y-2"
          style={{ 
            transition: 'opacity 0.45s ease, transform 0.45s ease',
            transitionDelay: '150ms',
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(14px)'
          }}
        >
          {sections.map((section) => (
            <div key={section.id}>
              <button
                onClick={() => isLocked ? toggleExpand(section.id) : navigate(section.path)}
                className={`w-full ${section.icon === 'sparkles' ? 'bg-[#FFFBF0] border border-amber-200' : 'bg-gray-50 border border-gray-100'} rounded-xl px-4 py-3 text-left transition-colors ${
                  isLocked ? 'hover:bg-gray-100' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {section.icon === 'sparkles' && <Sparkles size={14} className="text-amber-500 flex-shrink-0" />}
                    <span className="text-[13px] font-bold text-orella-700">{section.name}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md flex items-center gap-1 ${
                      section.complete 
                        ? 'text-green-600 bg-green-100' 
                        : 'text-amber-500 bg-amber-50'
                    }`}>
                      {section.complete && (
                        <Check size={10} strokeWidth={3} />
                      )}
                      {section.badge}
                    </span>
                  </div>
                  {!isLocked ? (
                    <span className="text-[12px] font-semibold text-orella-500 flex-shrink-0 ml-2">{t('describeReview.edit')}</span>
                  ) : (
                    expandedSection === section.id 
                      ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0 ml-2" />
                      : <ChevronRight size={16} className="text-gray-400 flex-shrink-0 ml-2" />
                  )}
                </div>
                {section.summary && expandedSection !== section.id && (
                  <p className="text-[12px] text-gray-400 mt-1 leading-relaxed truncate">{section.summary}</p>
                )}
              </button>
              {/* v164.3.0: Expanded detail view for locked/submitted state */}
              {isLocked && expandedSection === section.id && (
                <div className="px-2 pb-1" style={{ animation: 'fadeUp 0.2s ease' }}>
                  {renderExpandedContent(section)}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Spacer for scroll */}
        <div className="h-4" />
      </div>

      {/* Bottom CTA */}
      <div 
        className="p-4 pb-7 flex-shrink-0 bg-white border-t border-gray-100"
        style={{ 
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          transitionDelay: '300ms',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(8px)'
        }}
      >
        {isLocked ? (
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate('/case/describe/summary')}
              className="flex-1 py-3 rounded-full font-bold text-white text-[14px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform breathe-navy"
              style={{ backgroundColor: NAVY }}
            >
              <FileText size={15} />
              {t('describe.viewSummary') || 'View Summary'}
            </button>
            <button
              onClick={() => navigate('/case/home')}
              className="flex-1 py-3 rounded-full font-bold text-[14px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform border-[1.5px]"
              style={{ backgroundColor: 'white', color: NAVY, borderColor: '#D8E6F0' }}
            >
              {t('caseHome.backToCaseHome')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors"
            >
              <Trash2 size={16} className="text-red-400" />
            </button>
            <button
              onClick={handleViewSummary}
              disabled={navigatingToSummary}
              className={`flex-1 py-3.5 rounded-full font-bold text-white text-[15px] flex items-center justify-center gap-2 active:scale-[0.98] transition-all ${!navigatingToSummary ? 'breathe-blue' : ''}`}
              style={{ backgroundColor: navigatingToSummary ? '#46A8F9' : NAVY }}
            >
              <span className="relative flex items-center gap-2">
              {navigatingToSummary ? (
                <>
                  <OrellaSpinner size={16} tone="inherit" />
                  {t('common.loading') || 'Loading...'}
                </>
              ) : (
                <>
                  <Eye size={16} />
                  {hasSummary ? t('describe.updateMySummary') : (t('describe.viewCaseSummary') || 'View Summary')}
                </>
              )}
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)}
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onDelete={handleDeleteCase}
        onSaveAsDraft={handleSaveAsDraft}
        caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''}
      />
    </div>
  )
}
